import { useState, useMemo } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { Tabs, TabsContent, TabsList, TabsTrigger, Button, Card } from "@aix/ui"
import { ReactTable } from "@aix/ui"
import toast from "react-hot-toast"
import dayjs from "dayjs"
import { trpc } from "../../lib/trpc"

type TabValue = "summary" | "insights" | "debug"

interface Insight {
  id: string
  coachId: string
  insightType: "pattern" | "win" | "blocker" | "suggestion" | "celebration"
  title: string
  description: string
  confidence: "high" | "medium" | "low" | null
  userFeedback: "accepted" | "rejected" | "implemented" | "ignored" | null
  periodStartDate: string | null
  periodEndDate: string | null
  createdAt: Date
}

export default function WeeklyReviewScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { tab?: string; coachId?: string }

  // Initialize tab from query param or default to "summary"
  const initialTab = (search.tab as TabValue) || "summary"
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab)
  const [selectedCoachId, setSelectedCoachId] = useState<string | undefined>(search.coachId)

  // Week selector state
  const [weekStartDate, setWeekStartDate] = useState(() => {
    const today = dayjs()
    const startOfWeek = today.startOf("week")
    return startOfWeek.format("YYYY-MM-DD")
  })
  const [weekEndDate, setWeekEndDate] = useState(() => {
    const today = dayjs()
    const endOfWeek = today.endOf("week")
    return endOfWeek.format("YYYY-MM-DD")
  })

  // Fetch coaches
  const { data: coachesData, isLoading: coachesLoading } = trpc.coaches.list.useQuery({})

  // Fetch weekly plans for the selected week
  const { data: weeklyPlansData, refetch: refetchWeeklyPlans } = trpc.plans.weeklyPlans.list.useQuery(
    {
      coachId: selectedCoachId || "",
    },
    {
      enabled: !!selectedCoachId,
    },
  )

  // Fetch insights
  const {
    data: insightsData,
    refetch: refetchInsights,
    isLoading: insightsLoading,
  } = trpc.insights.list.useQuery({
    coachId: selectedCoachId,
    periodStartDate: weekStartDate,
    periodEndDate: weekEndDate,
  })

  // Mutations
  const generateInsights = trpc.insights.generate.useMutation()
  const updateInsightFeedback = trpc.insights.updateFeedback.useMutation()
  const generateWeeklyPlan = trpc.plans.weeklyPlans.generate.useMutation()

  const coaches = coachesData?.coaches || []
  const insights = insightsData?.insights || []

  // Filter insights by type
  const [insightTypeFilter, setInsightTypeFilter] = useState<string | null>(null)

  const filteredInsights = useMemo(() => {
    if (!insightTypeFilter) return insights
    return insights.filter((i) => i.insightType === insightTypeFilter)
  }, [insights, insightTypeFilter])

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue)
    navigate({
      to: "/coach-os/weekly-review",
      // @ts-ignore
      search: (s) => ({ ...s, tab: value }),
    })
  }

  const handleGenerateInsights = async () => {
    if (!selectedCoachId) {
      toast.error("Please select a coach first")
      return
    }

    try {
      await generateInsights.mutateAsync({
        coachId: selectedCoachId,
        periodStartDate: weekStartDate,
        periodEndDate: weekEndDate,
        periodType: "weekly",
      })
      toast.success("Insights generated successfully!")
      void refetchInsights()
    } catch (error: any) {
      toast.error(error.message || "Failed to generate insights")
    }
  }

  const handleInsightFeedback = async (
    insightId: string,
    feedback: "accepted" | "rejected" | "implemented" | "ignored",
  ) => {
    try {
      await updateInsightFeedback.mutateAsync({
        insightId,
        feedback,
      })
      toast.success("Feedback saved")
      void refetchInsights()
    } catch (error: any) {
      toast.error(error.message || "Failed to save feedback")
    }
  }

  const handlePlanNextWeek = async () => {
    if (!selectedCoachId) {
      toast.error("Please select a coach first")
      return
    }

    const nextWeekStart = dayjs(weekStartDate).add(7, "days").format("YYYY-MM-DD")
    const nextWeekEnd = dayjs(weekEndDate).add(7, "days").format("YYYY-MM-DD")

    try {
      await generateWeeklyPlan.mutateAsync({
        coachId: selectedCoachId,
        weekNumber: 1, // TODO: Calculate proper week number
        weekStartDate: nextWeekStart,
        weekEndDate: nextWeekEnd,
      })
      toast.success("Next week's plan generated!")
      void refetchWeeklyPlans()
    } catch (error: any) {
      toast.error(error.message || "Failed to generate plan")
    }
  }

  const handlePreviousWeek = () => {
    const prevStart = dayjs(weekStartDate).subtract(7, "days").format("YYYY-MM-DD")
    const prevEnd = dayjs(weekEndDate).subtract(7, "days").format("YYYY-MM-DD")
    setWeekStartDate(prevStart)
    setWeekEndDate(prevEnd)
  }

  const handleNextWeek = () => {
    const nextStart = dayjs(weekStartDate).add(7, "days").format("YYYY-MM-DD")
    const nextEnd = dayjs(weekEndDate).add(7, "days").format("YYYY-MM-DD")
    setWeekStartDate(nextStart)
    setWeekEndDate(nextEnd)
  }

  // Insights table columns
  const insightsColumns: any[] = [
    {
      accessorKey: "insightType",
      header: "Type",
      cell: ({ row }) => {
        const typeEmojis: Record<string, string> = {
          pattern: "🔄",
          win: "🏆",
          blocker: "🚧",
          suggestion: "💡",
          celebration: "🎉",
        }
        return (
          <span>
            {typeEmojis[row.original.insightType]} {row.original.insightType}
          </span>
        )
      },
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <div>
          <div className="font-semibold text-foreground">{row.original.title}</div>
          <div className="text-sm text-muted-foreground mt-1">{row.original.description}</div>
        </div>
      ),
    },
    {
      accessorKey: "confidence",
      header: "Confidence",
      cell: ({ row }) => {
        const confidenceColors: Record<string, string> = {
          high: "text-green-600",
          medium: "text-yellow-600",
          low: "text-red-600",
        }
        return (
          <span className={confidenceColors[row.original.confidence || "medium"]}>
            {row.original.confidence || "N/A"}
          </span>
        )
      },
    },
    {
      accessorKey: "userFeedback",
      header: "Feedback",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={row.original.userFeedback === "accepted" ? "default" : "outline"}
            onClick={() => handleInsightFeedback(row.original.id, "accepted")}
            disabled={updateInsightFeedback.isLoading}
          >
            ✓ Accept
          </Button>
          <Button
            size="sm"
            variant={row.original.userFeedback === "implemented" ? "default" : "outline"}
            onClick={() => handleInsightFeedback(row.original.id, "implemented")}
            disabled={updateInsightFeedback.isLoading}
          >
            ✓ Implemented
          </Button>
          <Button
            size="sm"
            variant={row.original.userFeedback === "rejected" ? "default" : "outline"}
            onClick={() => handleInsightFeedback(row.original.id, "rejected")}
            disabled={updateInsightFeedback.isLoading}
          >
            ✗ Reject
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b-4 border-border bg-secondary p-4 md:p-6">
        <h1 className="text-2xl md:text-3xl font-bold text-primary">📊 Weekly Review & Insights</h1>
        <p className="text-xs md:text-sm text-muted-foreground mt-2">
          Review your weekly performance and get AI-powered insights
        </p>
      </div>

      {/* Week Selector */}
      <div className="p-3 md:p-6 bg-background border-b-2 border-border">
        <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4">
          <Button onClick={handlePreviousWeek} variant="outline" className="w-full sm:w-auto text-xs md:text-sm">
            ← Previous
          </Button>
          <div className="flex-1 text-center">
            <span className="text-base md:text-lg font-semibold">
              {dayjs(weekStartDate).format("MMM D")} - {dayjs(weekEndDate).format("MMM D, YYYY")}
            </span>
          </div>
          <Button onClick={handleNextWeek} variant="outline" className="w-full sm:w-auto text-xs md:text-sm">
            Next →
          </Button>
        </div>

        {/* Coach Selector */}
        <div className="mt-4">
          <label className="text-xs md:text-sm font-medium text-muted-foreground mb-2 block">Select Coach:</label>
          <select
            value={selectedCoachId || ""}
            onChange={(e) => setSelectedCoachId(e.target.value || undefined)}
            className="w-full px-2 md:px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm md:text-base"
          >
            <option value="">All Coaches</option>
            {coaches.map((coach) => (
              <option key={coach.id} value={coach.id}>
                {coach.icon} {coach.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
          <div className="border-b-2 border-border bg-background px-6">
            <TabsList className="bg-transparent">
              <TabsTrigger
                value="summary"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                📋 Weekly Summary
              </TabsTrigger>
              <TabsTrigger
                value="insights"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                💡 Insights
              </TabsTrigger>
              <TabsTrigger
                value="debug"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                🐛 Debug
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            {/* Summary Tab */}
            <TabsContent value="summary" className="h-full m-0 p-3 md:p-6">
              <div className="space-y-4 md:space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">Weekly Summary</h2>
                  <Button
                    onClick={handlePlanNextWeek}
                    loading={generateWeeklyPlan.isLoading}
                    className="w-full sm:w-auto text-xs md:text-sm"
                  >
                    Plan Next Week
                  </Button>
                </div>

                {!selectedCoachId ? (
                  <Card className="p-4 md:p-6">
                    <p className="text-muted-foreground text-center text-sm md:text-base">
                      Please select a coach to view weekly summary
                    </p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {coaches
                      .filter((c) => !selectedCoachId || c.id === selectedCoachId)
                      .map((coach) => {
                        const coachPlans = weeklyPlansData?.weeklyPlans.filter((wp) => wp.coachId === coach.id) || []
                        const currentWeekPlan = coachPlans.find((wp) => wp.weekStartDate === weekStartDate)

                        return (
                          <Card key={coach.id} className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <span className="text-4xl">{coach.icon}</span>
                              <div>
                                <h3 className="text-lg font-bold text-foreground">{coach.name}</h3>
                                <span className="text-sm text-muted-foreground">{coach.type}</span>
                              </div>
                            </div>

                            {currentWeekPlan ? (
                              <div className="space-y-3">
                                <div>
                                  <span className="text-sm text-muted-foreground">Completion:</span>
                                  <div className="flex items-center gap-2 mt-1">
                                    <div className="flex-1 bg-secondary rounded-full h-2">
                                      <div
                                        className="bg-primary h-2 rounded-full transition-all"
                                        style={{
                                          width: `${
                                            currentWeekPlan.totalActions > 0
                                              ? (currentWeekPlan.completionCount / currentWeekPlan.totalActions) * 100
                                              : 0
                                          }%`,
                                        }}
                                      />
                                    </div>
                                    <span className="text-sm font-semibold">
                                      {currentWeekPlan.completionCount} / {currentWeekPlan.totalActions}
                                    </span>
                                  </div>
                                </div>

                                <div>
                                  <span className="text-sm text-muted-foreground">Status:</span>
                                  <span className="ml-2 text-sm font-semibold">{currentWeekPlan.status}</span>
                                </div>

                                {currentWeekPlan.phaseName && (
                                  <div>
                                    <span className="text-sm text-muted-foreground">Phase:</span>
                                    <span className="ml-2 text-sm font-semibold">{currentWeekPlan.phaseName}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No plan for this week</p>
                            )}
                          </Card>
                        )
                      })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="h-full m-0 p-3 md:p-6">
              <div className="space-y-4 md:space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground">AI Insights</h2>
                  <Button
                    onClick={handleGenerateInsights}
                    loading={generateInsights.isLoading}
                    className="w-full sm:w-auto text-xs md:text-sm"
                  >
                    Generate Insights
                  </Button>
                </div>

                {/* Insight Type Filter */}
                <div className="flex gap-1 md:gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant={!insightTypeFilter ? "default" : "outline"}
                    onClick={() => setInsightTypeFilter(null)}
                  >
                    All
                  </Button>
                  <Button
                    size="sm"
                    variant={insightTypeFilter === "pattern" ? "default" : "outline"}
                    onClick={() => setInsightTypeFilter("pattern")}
                  >
                    🔄 Patterns
                  </Button>
                  <Button
                    size="sm"
                    variant={insightTypeFilter === "win" ? "default" : "outline"}
                    onClick={() => setInsightTypeFilter("win")}
                  >
                    🏆 Wins
                  </Button>
                  <Button
                    size="sm"
                    variant={insightTypeFilter === "blocker" ? "default" : "outline"}
                    onClick={() => setInsightTypeFilter("blocker")}
                  >
                    🚧 Blockers
                  </Button>
                  <Button
                    size="sm"
                    variant={insightTypeFilter === "suggestion" ? "default" : "outline"}
                    onClick={() => setInsightTypeFilter("suggestion")}
                  >
                    💡 Suggestions
                  </Button>
                </div>

                {/* Insights Table */}
                <div className="border-2 border-border rounded-lg overflow-hidden">
                  <ReactTable
                    data={filteredInsights}
                    columns={insightsColumns}
                    loading={insightsLoading}
                    emptyMessage="No insights found. Generate insights to see AI-powered recommendations."
                    {...({} as any)}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Debug Tab */}
            <TabsContent value="debug" className="h-full m-0 p-6">
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-foreground">Debug Data</h2>

                <Card className="p-6">
                  <h3 className="text-lg font-bold mb-4">Coaches Data</h3>
                  <pre className="text-xs bg-secondary p-4 rounded-md overflow-auto max-h-96">
                    {JSON.stringify(coaches, null, 2)}
                  </pre>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-bold mb-4">Weekly Plans Data</h3>
                  <pre className="text-xs bg-secondary p-4 rounded-md overflow-auto max-h-96">
                    {JSON.stringify(weeklyPlansData, null, 2)}
                  </pre>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-bold mb-4">Insights Data</h3>
                  <pre className="text-xs bg-secondary p-4 rounded-md overflow-auto max-h-96">
                    {JSON.stringify(insightsData, null, 2)}
                  </pre>
                </Card>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}
